import React, { useState, useMemo } from 'react';
import { X, RefreshCw, DollarSign, TrendingDown, Calendar } from 'lucide-react';
import { calculatePayment } from '../utils/calculations';

const CompareScenarios = ({ mortgage, onClose }) => {
  const [scenarios, setScenarios] = useState([
    {
      id: 1,
      name: 'Current',
      balance: mortgage.current_balance || 0,
      rate: mortgage.current_rate || 0,
      years: mortgage.amortization_remain || 20,
      frequency: mortgage.payment_frequency || 'monthly',
      prepayment: 0,
      prepaymentFrequency: 'monthly'
    },
    {
      id: 2,
      name: 'Scenario A',
      balance: mortgage.current_balance || 0,
      rate: mortgage.current_rate || 0,
      years: mortgage.amortization_remain || 20,
      frequency: 'biweekly',
      prepayment: 0,
      prepaymentFrequency: 'monthly'
    },
    {
      id: 3,
      name: 'Scenario B',
      balance: mortgage.current_balance || 0,
      rate: mortgage.current_rate ? mortgage.current_rate - 0.5 : 0,
      years: mortgage.amortization_remain || 20,
      frequency: 'monthly',
      prepayment: 0,
      prepaymentFrequency: 'monthly'
    }
  ]);
  
  const updateScenario = (id, field, value) => {
    setScenarios(scenarios.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  };
  
  const calculateScenarioMetrics = (scenario) => {
    if (!scenario.balance || !scenario.rate || !scenario.years) {
      return null;
    }
    
    const payment = calculatePayment(scenario.balance, scenario.rate, scenario.years, scenario.frequency);
    const periodsPerYear = scenario.frequency === 'monthly' ? 12 : scenario.frequency === 'biweekly' ? 26 : 52;
    const totalPeriods = scenario.years * periodsPerYear;
    const totalPaid = payment.amount * totalPeriods;
    const totalInterest = totalPaid - scenario.balance;
    
    // Calculate with prepayment
    let adjustedYears = scenario.years;
    let adjustedInterest = totalInterest;
    
    if (scenario.prepayment > 0) {
      const prepaymentPerYear = scenario.prepaymentFrequency === 'monthly' 
        ? scenario.prepayment * 12 
        : scenario.prepaymentFrequency === 'annually'
        ? scenario.prepayment
        : scenario.prepayment * 26;
      
      const regularPaymentYearly = payment.monthlyEquivalent * 12;
      const totalAnnualPayment = regularPaymentYearly + prepaymentPerYear;
      
      // Rough estimate of years saved
      const yearsSaved = Math.max(0, scenario.years * (1 - (regularPaymentYearly / totalAnnualPayment)) * 0.3);
      adjustedYears = Math.max(1, scenario.years - yearsSaved);
      adjustedInterest = totalInterest * (adjustedYears / scenario.years);
    }
    
    return {
      monthlyPayment: payment.monthlyEquivalent,
      paymentAmount: payment.amount,
      totalPaid,
      totalInterest,
      adjustedYears,
      adjustedInterest,
      interestSavings: totalInterest - adjustedInterest,
      prepaymentTotal: scenario.prepayment > 0 ? (
        scenario.prepaymentFrequency === 'monthly' 
          ? scenario.prepayment * 12 * adjustedYears
          : scenario.prepaymentFrequency === 'annually'
          ? scenario.prepayment * adjustedYears
          : scenario.prepayment * 26 * adjustedYears
      ) : 0
    };
  };
  
  const scenarioMetrics = useMemo(() => {
    return scenarios.map(s => ({
      ...s,
      metrics: calculateScenarioMetrics(s)
    }));
  }, [scenarios]);
  
  const bestScenario = useMemo(() => {
    const validScenarios = scenarioMetrics.filter(s => s.metrics);
    if (validScenarios.length === 0) return null;
    
    return validScenarios.reduce((best, current) => 
      current.metrics.adjustedInterest < best.metrics.adjustedInterest ? current : best
    );
  }, [scenarioMetrics]);
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h3 className="text-2xl font-heading font-semibold flex items-center gap-2">
              <RefreshCw className="h-6 w-6 text-primary" />
              Compare Scenarios
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Compare different payment strategies side-by-side
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {scenarios.map((scenario) => {
              const metrics = scenarioMetrics.find(s => s.id === scenario.id)?.metrics;
              const isBest = bestScenario?.id === scenario.id;
              
              return (
                <div
                  key={scenario.id}
                  className={`bg-background border-2 rounded-xl p-6 ${
                    isBest ? 'border-primary' : 'border-border'
                  }`}
                >
                  {isBest && (
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary text-xs rounded-full mb-3">
                      <TrendingDown className="h-3 w-3" />
                      Lowest Interest
                    </div>
                  )}
                  
                  <input
                    type="text"
                    value={scenario.name}
                    onChange={(e) => updateScenario(scenario.id, 'name', e.target.value)}
                    className="w-full text-lg font-semibold mb-4 bg-transparent border-b border-border focus:border-primary outline-none pb-1"
                  />
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium mb-1.5 text-muted-foreground">
                        Interest Rate (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={scenario.rate}
                        onChange={(e) => updateScenario(scenario.id, 'rate', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 bg-card border border-border rounded-lg focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium mb-1.5 text-muted-foreground">
                        Years Remaining
                      </label>
                      <input
                        type="number"
                        value={scenario.years}
                        onChange={(e) => updateScenario(scenario.id, 'years', parseInt(e.target.value))}
                        className="w-full px-3 py-2 bg-card border border-border rounded-lg focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium mb-1.5 text-muted-foreground">
                        Payment Frequency
                      </label>
                      <select
                        value={scenario.frequency}
                        onChange={(e) => updateScenario(scenario.id, 'frequency', e.target.value)}
                        className="w-full px-3 py-2 bg-card border border-border rounded-lg focus:ring-2 focus:ring-primary"
                      >
                        <option value="monthly">Monthly</option>
                        <option value="biweekly">Bi-weekly</option>
                        <option value="weekly">Weekly</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium mb-1.5 text-muted-foreground">
                        Extra Prepayment ($)
                      </label>
                      <input
                        type="number"
                        value={scenario.prepayment}
                        onChange={(e) => updateScenario(scenario.id, 'prepayment', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 bg-card border border-border rounded-lg focus:ring-2 focus:ring-primary"
                        placeholder="0"
                      />
                    </div>
                    
                    {scenario.prepayment > 0 && (
                      <div>
                        <label className="block text-xs font-medium mb-1.5 text-muted-foreground">
                          Prepayment Frequency
                        </label>
                        <select
                          value={scenario.prepaymentFrequency}
                          onChange={(e) => updateScenario(scenario.id, 'prepaymentFrequency', e.target.value)}
                          className="w-full px-3 py-2 bg-card border border-border rounded-lg focus:ring-2 focus:ring-primary"
                        >
                          <option value="monthly">Monthly</option>
                          <option value="annually">Annually</option>
                        </select>
                      </div>
                    )}
                  </div>
                  
                  {/* Metrics */}
                  {metrics && (
                    <div className="mt-6 pt-6 border-t border-border space-y-3">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Monthly Payment</p>
                        <p className="text-2xl font-bold font-mono">
                          ${metrics.monthlyPayment.toLocaleString('en-CA', {maximumFractionDigits: 2})}
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Total Interest</p>
                          <p className="font-mono font-semibold text-red-400">
                            ${metrics.adjustedInterest.toLocaleString('en-CA', {maximumFractionDigits: 0})}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Years to Payoff</p>
                          <p className="font-mono font-semibold">
                            {metrics.adjustedYears.toFixed(1)}
                          </p>
                        </div>
                      </div>
                      
                      {scenario.prepayment > 0 && (
                        <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
                          <p className="text-xs font-semibold text-primary mb-1">
                            Interest Saved
                          </p>
                          <p className="font-mono font-bold text-primary">
                            ${metrics.interestSavings.toLocaleString('en-CA', {maximumFractionDigits: 0})}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Comparison Summary */}
          {scenarioMetrics.every(s => s.metrics) && (
            <div className="mt-8 bg-primary/10 border border-primary/20 rounded-xl p-6">
              <h4 className="text-lg font-semibold mb-4">Comparison Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {scenarioMetrics.map((scenario) => (
                  <div key={scenario.id}>
                    <p className="text-sm font-medium mb-2">{scenario.name}</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Interest:</span>
                        <span className="font-mono">
                          ${scenario.metrics.adjustedInterest.toLocaleString('en-CA', {maximumFractionDigits: 0})}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Years:</span>
                        <span className="font-mono">{scenario.metrics.adjustedYears.toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {bestScenario && (
                <div className="mt-4 pt-4 border-t border-primary/20">
                  <p className="text-sm">
                    <strong className="text-primary">{bestScenario.name}</strong> saves you the most interest 
                    with a total of <strong className="text-primary">
                      ${bestScenario.metrics.adjustedInterest.toLocaleString('en-CA', {maximumFractionDigits: 0})}
                    </strong> in interest over {bestScenario.metrics.adjustedYears.toFixed(1)} years.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t border-border p-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompareScenarios;
